import React from "react";
import {
  listSystemPermissions,
  getRoleSystemPermissions,
  replaceRoleSystemPermissions,
  RolePermissionAssignment,
  RolePermissionMatrix,
  SystemPermissionDto,
} from "../../services/systemPermissions.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import Button from "../../components/Button.tsx";
import {
  moduleLabel,
  permissionCopy,
  scopeLabel,
  SCOPE_ORDER,
  SCOPE_RESTRICTION,
} from "./permissionCopy.ts";
import {
  assignmentsMatch,
  draftValidationError,
  enabledAssignments,
  PermissionDraft,
  permissionSaveMessage,
  removalConfirmMessage,
  removedAssignments,
  rowScopeError,
} from "./permissionSave.ts";

const RolePermissionEditor = ({
  roleId,
  canEdit,
}: {
  roleId: number;
  canEdit: boolean;
}) => {
  const [catalog, setCatalog] = React.useState<SystemPermissionDto[]>([]);
  const [draft, setDraft] = React.useState<PermissionDraft>({});
  const [baselineDraft, setBaselineDraft] = React.useState<PermissionDraft>({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [structural, setStructural] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [savedOk, setSavedOk] = React.useState(false);

  const applyMatrix = (
    permissions: SystemPermissionDto[],
    matrix: RolePermissionMatrix,
  ) => {
    const next: PermissionDraft = {};
    permissions.forEach((item) => {
      next[item.code] = { enabled: false, scope: "global" };
    });
    (matrix.assignments || []).forEach((row: RolePermissionAssignment) => {
      next[row.code] = { enabled: true, scope: row.scope || "global" };
    });
    setDraft(next);
    setBaselineDraft(next);
    setStructural(matrix.structural === true);
  };

  const load = async () => {
    setLoading(true);
    setSaveError("");
    setSavedOk(false);
    try {
      const [permissions, matrix] = await Promise.all([
        listSystemPermissions(),
        getRoleSystemPermissions(roleId),
      ]);
      setCatalog(permissions);
      applyMatrix(permissions, matrix);
    } catch (error) {
      const message = permissionSaveMessage(error);
      setSaveError(message);
      notification("Error", message, "danger");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(baselineDraft);
  const validationError = draftValidationError(draft, baselineDraft);
  const grouped = catalog.reduce<Record<string, SystemPermissionDto[]>>(
    (acc, item) => {
      acc[item.module] = acc[item.module] || [];
      acc[item.module].push(item);
      return acc;
    },
    {},
  );
  const extraCodes = Object.keys(draft).filter(
    (code) => draft[code]?.enabled && !catalog.some((item) => item.code === code),
  );

  const save = async () => {
    const problem = draftValidationError(draft, baselineDraft);
    if (problem) {
      setSavedOk(false);
      setSaveError(problem);
      notification("Error", problem, "danger");
      return;
    }
    const permissions = enabledAssignments(draft);
    const removed = removedAssignments(baselineDraft, draft);
    const confirmText = removalConfirmMessage(removed);
    if (confirmText && !window.confirm(confirmText)) {
      return;
    }
    setSaving(true);
    setSaveError("");
    setSavedOk(false);
    try {
      await replaceRoleSystemPermissions(roleId, permissions);
      const reread = await getRoleSystemPermissions(roleId);
      if (!assignmentsMatch(permissions, reread.assignments)) {
        applyMatrix(catalog, reread);
        const mismatch = permissionSaveMessage({
          response: { status: 500, data: { code: "SAVE_MISMATCH" } },
        });
        setSaveError(mismatch);
        notification("Error", mismatch, "danger");
        return;
      }
      applyMatrix(catalog, reread);
      setSavedOk(true);
      notification("Éxito", "Permisos actualizados", "success");
    } catch (error) {
      const message = permissionSaveMessage(error);
      setSavedOk(false);
      setSaveError(message);
      notification("Error", message, "danger");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-secondary">Cargando permisos…</p>;
  }

  if (structural) {
    return (
      <div className="alert alert-info">
        Este rol tiene acceso a todo el sistema. Sus permisos no se editan
        desde esta pantalla.
      </div>
    );
  }

  return (
    <div>
      <div className="alert alert-warning">
        Al guardar se reemplaza la lista completa de este rol. Los permisos
        que desmarques se quitarán. El alcance indica sobre qué registros
        puede actuar la persona.
      </div>
      {saveError && (
        <div className="alert alert-danger" role="alert">
          {saveError}
        </div>
      )}
      {savedOk && !dirty && (
        <div className="alert alert-success" role="status">
          Permisos guardados.
        </div>
      )}
      {Object.keys(grouped)
        .sort((left, right) => moduleLabel(left).localeCompare(moduleLabel(right), "es"))
        .map((moduleName) => (
          <div key={moduleName} className="mb-3">
            <h6 className="fs-12px">{moduleLabel(moduleName)}</h6>
            {grouped[moduleName].map((item) => {
              const row = draft[item.code] || { enabled: false, scope: "global" };
              const copy = permissionCopy(item.code);
              const scopeError = rowScopeError(
                item.code,
                row.scope,
                row.enabled,
                baselineDraft,
              );
              const scopeOptions = (SCOPE_ORDER as string[]).includes(row.scope)
                ? [...SCOPE_ORDER]
                : [row.scope, ...SCOPE_ORDER];
              return (
                <div key={item.code} className="border rounded p-2 mb-2">
                  <label className="form-check mb-1">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      disabled={!canEdit}
                      checked={row.enabled}
                      onChange={(event) => {
                        setSavedOk(false);
                        setDraft((current) => ({
                          ...current,
                          [item.code]: {
                            ...row,
                            enabled: event.target.checked,
                          },
                        }));
                      }}
                    />
                    <span className="form-check-label ms-1 fw-semibold">
                      {copy.name}
                    </span>
                  </label>
                  <div className="text-secondary small">{copy.description}</div>
                  {row.enabled && (
                    <div className="mt-2">
                      <label className="form-label small mb-1">
                        A qué registros tendrá acceso
                      </label>
                      <select
                        className="form-select form-select-sm w-auto"
                        disabled={!canEdit}
                        value={row.scope}
                        onChange={(event) => {
                          setSavedOk(false);
                          setDraft((current) => ({
                            ...current,
                            [item.code]: { ...row, scope: event.target.value },
                          }));
                        }}
                      >
                        {scopeOptions.map((scope) => (
                          <option key={scope} value={scope}>
                            {scopeLabel(scope) || scope}
                          </option>
                        ))}
                      </select>
                      <div className="small mt-1">
                        Alcance: {scopeLabel(row.scope) || "Selecciona a qué registros tendrá acceso."}
                      </div>
                      {SCOPE_RESTRICTION[row.scope as keyof typeof SCOPE_RESTRICTION] && (
                        <div className="small text-secondary">
                          {copy.description}{" "}
                          {SCOPE_RESTRICTION[row.scope as keyof typeof SCOPE_RESTRICTION]}
                        </div>
                      )}
                      {copy.sensitive && (
                        <div className="small text-warning">
                          Este permiso permite acciones delicadas. Confírmalo
                          antes de quitarlo.
                        </div>
                      )}
                      {scopeError && (
                        <div className="small text-danger">{scopeError}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      {extraCodes.length > 0 && (
        <div className="mb-3">
          <h6 className="fs-12px">Otros permisos ya asignados</h6>
          {extraCodes.map((code) => {
            const row = draft[code];
            const copy = permissionCopy(code);
            return (
              <div key={code} className="border rounded p-2 mb-2">
                <label className="form-check mb-0">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    disabled={!canEdit}
                    checked={row.enabled}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        [code]: { ...row, enabled: event.target.checked },
                      }))
                    }
                  />
                  <span className="form-check-label ms-1 fw-semibold">
                    {copy.name}
                  </span>
                </label>
                <div className="small mt-1">
                  Alcance: {scopeLabel(row.scope) || row.scope}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {canEdit && (
        <div className="d-flex flex-column align-items-end gap-2">
          {validationError && dirty && (
            <div className="text-danger small">{validationError}</div>
          )}
          <div className="col-md-3">
            <Button
              type="button"
              variant="azul"
              title={
                saving ? "Guardando…" : dirty ? "Guardar cambios" : "Sin cambios"
              }
              disabled={saving || !dirty || Boolean(validationError)}
              onClick={save}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissionEditor;
